// src/pages/ValmikiHome.jsx
// VALMIKI AI LAB — Home page (Page 1 of the 6-page Phase 1 architecture)
// ONE self-contained file: Navbar + Footer + all CSS live here.
// No external .css file, no MUI, no images required.
// Lead popup: <ValmikiLeadForm /> opens itself after its own timer (see that file).

import React, { useEffect, useState } from "react";
import ValmikiFooter from "./ValmikiFooter";
import ValmikiLeadForm from "./ValmikiLeadPopup";

/* ───────────────────────── CONFIG ───────────────────────── */

// Drop in a real photo (import heroPhoto from "../images/valmiki-hero.jpg") to show it
// behind the evidence panel. Left empty, the hero shows the panel on its own.
const HERO_IMAGE = "";
const HERO_IMAGE_ALT = "Students collaborating on a project at a VALMIKI AI LAB session";

/* ───────────────────────── CONTENT ───────────────────────── */

// Illustrative only — mirrors "What You Build" and the internship description in the docx.
const evidenceRows = [
  { title: "AI-assisted workflow", state: "documented" },
  { title: "Automation project", state: "milestone reviewed" },
  { title: "Agentic AI workflow", state: "tested" },
  { title: "Capstone project", state: "presented" },
];

// Expanded: each approach item now has a title + description for the "+" toggle.
const approach = [
  {
    title: "Structured learning",
    desc: "A clear, sequenced curriculum that builds AI capability step by step from fundamentals to applied practice.",
  },
  {
    title: "Practical labs",
    desc: "Hands-on lab sessions where students work directly with modern AI tools, models and workflows.",
  },
  {
    title: "Project-based learning",
    desc: "Every concept is applied to a real project so students learn by building, not just by reading.",
  },
  {
    title: "Portfolio development",
    desc: "Each project is documented as portfolio evidence that students can show to employers.",
  },
  {
    title: "Internship experience",
    desc: "A structured industry internship that puts classroom skills into real working environments.",
  },
  {
    title: "Career readiness",
    desc: "Interview prep, communication skills and placement support to help students transition into AI Enabled roles.",
  },
];

const capabilities = [
  "Use AI effectively",
  "Design AI-assisted workflows",
  "Automate repetitive processes",
  "Build practical projects",
  "Communicate solutions professionally",
];

const programSegments = [
  { size: "4 months", label: "Applied classroom learning", w: "100%", tone: 1 },
  { size: "2 months", label: "Structured industry internship", w: "55%", tone: 2 },
  { size: "7 days", label: "AI career & placement bootcamp", w: "22%", tone: 3 },
];

const learnAreas = [
  { title: "Generative AI", desc: "Research, productivity and problem-solving." },
  { title: "Prompt Engineering", desc: "Structured and effective AI instructions." },
  { title: "AI Automation", desc: "Intelligent workflows and process improvement." },
  { title: "Agentic AI", desc: "Agents and multi-step workflows." },
];

const learningModel = [
  "Learn",
  "Practice",
  "Build",
  "Project",
  "Portfolio",
  "Internship",
  "Career bootcamp",
  "Career support",
];

const labFocus = [
  "Generative AI Lab",
  "AI Automation Lab",
  "Agentic AI Lab",
  "Creative AI Lab",
  "AI Innovation Lab",
];

/* ───────────────────────── STYLES (in-page) ───────────────────────── */

const css = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&display=swap');

.vl-root{
  --ink:#0B1626; --ink-2:#132238; --ink-3:#08111E;
  --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --on-ink-mute:rgba(243,245,248,.72);
  --line:rgba(11,22,38,.14); --line-ink:rgba(243,245,248,.16);
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,Menlo,Consolas,monospace;
  font-family:var(--sans); color:var(--ink); background:var(--paper);
  line-height:1.6; -webkit-font-smoothing:antialiased; margin:0;
}
.vl-root *{box-sizing:border-box;}
:where(.vl-root) :where(h1,h2,h3,p,ul,ol){margin:0;padding:0;}
:where(.vl-root) :where(ul,ol){list-style:none;}
:where(.vl-root) a{color:inherit;}
.vl-root :focus-visible{outline:2px solid var(--gold-hi);outline-offset:3px;}

.vl-wrap{max-width:1120px;margin:0 auto;padding:0 24px;}
.vl-section{padding:104px 0;}
.vl-dark{background:var(--ink);color:var(--on-ink);}
.vl-white{background:var(--white);}

.vl-h2{font-family:var(--serif);font-weight:500;font-size:clamp(2rem,4vw,3rem);line-height:1.12;letter-spacing:-.015em;}
.vl-h3{font-family:var(--serif);font-weight:500;font-size:1.5rem;line-height:1.2;}
.vl-lead{font-size:1.125rem;line-height:1.65;max-width:56ch;}
.vl-muted{color:var(--slate);}
.vl-dark .vl-muted{color:var(--on-ink-mute);}

@property --vl-a{syntax:"<angle>";inherits:false;initial-value:0deg;}
.vl-actions{display:flex;flex-wrap:wrap;gap:14px;}
.vl-btn{
  --fill:#162B47; --edge:var(--gold); --line:var(--gold);
  --wipe:var(--gold); --hover-text:var(--ink); --text:var(--on-ink);
  position:relative;overflow:hidden;isolation:isolate;
  display:inline-flex;align-items:center;justify-content:center;padding:14px 28px;border-radius:2px;
  font:600 15px/1 var(--sans);text-decoration:none;cursor:pointer;color:var(--text);
  border:1px solid var(--edge);
  background-color:var(--fill);
  background-image:linear-gradient(var(--wipe),var(--wipe));
  background-repeat:no-repeat;background-origin:border-box;background-position:left center;background-size:0% 100%;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s,border-color .3s ease;
}
.vl-btn::before{
  content:"";position:absolute;inset:0;padding:2px;border-radius:inherit;pointer-events:none;
  background:conic-gradient(from var(--vl-a),
    transparent 0deg,var(--line) 55deg,transparent 56deg,
    transparent 180deg,var(--line) 235deg,transparent 236deg);
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;
  mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  mask-composite:exclude;
  animation:vl-orbit 3.2s linear infinite;
}
@keyframes vl-orbit{to{--vl-a:360deg;}}
.vl-btn:hover,.vl-btn:focus-visible{background-size:100% 100%;color:var(--hover-text);border-color:var(--wipe);}

.vl-btn-gold{animation:vl-breathe 4s ease-in-out infinite;}
@keyframes vl-breathe{
  0%,100%{background-color:#162B47;box-shadow:0 6px 14px rgba(0,0,0,.35);}
  50%{background-color:#24426B;box-shadow:0 6px 18px rgba(200,162,74,.30);}
}

.vl-btn-line-light{--fill:transparent;--edge:var(--paper);--line:var(--paper);--wipe:var(--paper);--hover-text:var(--ink);}
.vl-btn-ink{--fill:var(--ink);--edge:var(--gold);--line:var(--gold);--wipe:var(--gold);--hover-text:var(--ink);--text:var(--paper);}
.vl-btn-line-ink{--fill:transparent;--edge:var(--ink);--line:var(--ink);--wipe:var(--ink);--hover-text:var(--paper);--text:var(--ink);}
.vl-final .vl-btn-ink{--edge:var(--paper);--line:var(--paper);--wipe:var(--paper);}
@media (prefers-reduced-motion:reduce){
  .vl-btn,.vl-toggle,.vl-rule-toggle{transition:none;}
  .vl-btn::before{animation:none;}
  .vl-btn-gold{animation:none;}
}

.vl-header{position:sticky;top:0;z-index:50;background:var(--ink);border-bottom:1px solid var(--line-ink);}
.vl-header-row{display:flex;align-items:center;justify-content:space-between;min-height:68px;gap:24px;}
.vl-brand{display:flex;align-items:center;gap:12px;text-decoration:none;color:var(--on-ink);
  font:600 17px/1 var(--sans);letter-spacing:.14em;}
.vl-nav{display:flex;align-items:center;gap:26px;}
.vl-nav a:not(.vl-btn){font-size:12.5px;font-weight:500;letter-spacing:.09em;text-decoration:none;color:var(--on-ink-mute);
  padding:6px 0;border-bottom:1.5px solid transparent;transition:color .15s,border-color .15s;}
.vl-nav a:not(.vl-btn):hover{color:var(--on-ink);}
.vl-nav a[aria-current="page"]{color:var(--on-ink);border-bottom-color:var(--gold);}
.vl-nav .vl-btn{padding:11px 20px;font-size:12.5px;letter-spacing:.09em;}
.vl-toggle{display:none;background-color:transparent;
  background-image:linear-gradient(var(--paper),var(--paper));background-repeat:no-repeat;
  background-origin:border-box;background-position:left center;background-size:0% 100%;
  border:1px solid var(--line-ink);color:var(--on-ink);
  width:42px;height:42px;border-radius:3px;font-size:20px;cursor:pointer;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s;}
.vl-toggle:hover,.vl-toggle:focus-visible{background-size:100% 100%;color:var(--ink);}
.vl-mobile{display:none;background:var(--ink);border-top:1px solid var(--line-ink);padding:8px 24px 22px;}
.vl-mobile a:not(.vl-btn){display:block;padding:14px 0;border-bottom:1px solid var(--line-ink);text-decoration:none;
  font-size:14px;letter-spacing:.08em;color:var(--on-ink);}
.vl-mobile .vl-btn{margin-top:18px;width:100%;}
@media (max-width:1000px){
  .vl-nav{display:none;}
  .vl-toggle{display:block;}
  .vl-mobile.open{display:block;}
}

.vl-hero{background-color:var(--ink);color:var(--on-ink);padding:88px 0 104px;
  background-image:linear-gradient(rgba(243,245,248,.035) 1px,transparent 1px),
                   linear-gradient(90deg,rgba(243,245,248,.035) 1px,transparent 1px);
  background-size:56px 56px;}
.vl-hero-grid{display:grid;grid-template-columns:1.2fr .8fr;gap:64px;align-items:center;}
.vl-h1{font-family:var(--serif);font-weight:500;font-size:clamp(2.4rem,5vw,4rem);line-height:1.05;
  letter-spacing:-.025em;margin-bottom:28px;}
.vl-h1 span{display:block;}
@media (min-width:861px){.vl-h1 span{white-space:nowrap;}}
.vl-hero .vl-lead{color:var(--on-ink-mute);margin-bottom:36px;}

.vl-hero{position:relative;overflow:hidden;}
.vl-hero-grid{position:relative;z-index:1;}
.vl-hero-fx{position:absolute;inset:0;pointer-events:none;overflow:hidden;}
.vl-hero-glow{position:absolute;right:-6%;top:6%;width:560px;height:560px;border-radius:50%;
  background:radial-gradient(circle,rgba(200,162,74,.18),transparent 65%);}
.vl-hex{position:absolute;fill:none;stroke:rgba(243,245,248,.09);stroke-width:1;}
.vl-hex-1{width:170px;left:-44px;bottom:8%;}
.vl-hex-2{width:96px;left:44%;top:9%;}
.vl-hex-3{width:240px;right:24%;bottom:-90px;}
.vl-dot{position:absolute;left:var(--x);top:var(--y);width:var(--s);height:var(--s);
  border-radius:50%;background:var(--gold-hi);opacity:.4;}
@media (prefers-reduced-motion:no-preference){
  .vl-hero{animation:vl-grid 10s linear infinite;}
  .vl-h1 span{animation:vl-rise .5s cubic-bezier(.2,.7,.2,1) both;animation-delay:calc(.05s + var(--i) * .1s);}
  .vl-hero .vl-lead{animation:vl-rise .5s cubic-bezier(.2,.7,.2,1) .4s both;}
  .vl-hero .vl-actions{animation:vl-rise .5s cubic-bezier(.2,.7,.2,1) .55s both;}
  .vl-log{animation:vl-panel-in .55s cubic-bezier(.2,.7,.2,1) .1s both,vl-float 3.5s ease-in-out .8s infinite;}
  .vl-hero-glow{animation:vl-glow 4s ease-in-out infinite alternate;}
  .vl-hex-1{animation:vl-drift 7s ease-in-out infinite alternate;}
  .vl-hex-2{animation:vl-drift 9s ease-in-out infinite alternate;}
  .vl-hex-3{animation:vl-drift 11s ease-in-out infinite alternate;}
  .vl-dot{opacity:0;animation:vl-twinkle calc(var(--dur) * .5) ease-in-out calc(var(--d) * .5) infinite;}
  .vl-hero .vl-btn::before{animation-duration:2s;}
  .vl-hero .vl-btn-gold{animation-duration:2.5s;}
}
@keyframes vl-grid{to{background-position:56px 56px,56px 56px;}}
@keyframes vl-rise{from{opacity:0;transform:translateY(22px);}to{opacity:1;transform:none;}}
@keyframes vl-panel-in{from{opacity:0;transform:translateX(28px);}to{opacity:1;transform:none;}}
@keyframes vl-float{0%,100%{transform:translateY(0);}50%{transform:translateY(-8px);}}
@keyframes vl-glow{to{transform:translate(-50px,40px) scale(1.12);}}
@keyframes vl-drift{from{transform:translate(0,0) rotate(0deg);}to{transform:translate(24px,-34px) rotate(28deg);}}
@keyframes vl-twinkle{0%,100%{opacity:.06;transform:scale(.7);}50%{opacity:.85;transform:scale(1.25);}}

.vl-visual{position:relative;}
.vl-photo{display:block;width:100%;aspect-ratio:4/3;object-fit:cover;border-radius:10px;}
.vl-log{background:var(--ink-2);border:1px solid var(--line-ink);border-radius:10px;padding:22px 24px 20px;
  box-shadow:0 24px 60px rgba(0,0,0,.35);}
.vl-visual.has-photo .vl-log{position:relative;margin:-64px 0 0 -28px;width:calc(100% - 20px);}
.vl-log-head{display:flex;justify-content:space-between;align-items:baseline;gap:12px;
  padding-bottom:14px;border-bottom:1px solid var(--line-ink);margin-bottom:6px;}
.vl-log-title{font:600 15px/1.3 var(--sans);}
.vl-log-tag{font:400 12px/1 var(--mono);color:var(--on-ink-mute);}
.vl-log-row{display:flex;align-items:center;gap:14px;padding:15px 0;border-bottom:1px solid var(--line-ink);}
.vl-log-row:last-of-type{border-bottom:0;}
.vl-tick{flex:none;width:22px;height:22px;border-radius:50%;border:1.5px solid var(--gold);
  display:flex;align-items:center;justify-content:center;}
.vl-tick svg{display:block;}
.vl-log-name{flex:1;font-size:15px;font-weight:500;}
.vl-log-state{font:400 12.5px/1 var(--mono);color:var(--gold-hi);text-align:right;}
.vl-log-foot{margin-top:6px;padding-top:14px;border-top:1px solid var(--line-ink);font-size:13px;color:var(--on-ink-mute);}
@media (prefers-reduced-motion:no-preference){
  .vl-log-row{opacity:0;transform:translateY(8px);
    animation:vl-in .55s cubic-bezier(.2,.7,.2,1) forwards;animation-delay:calc(.45s + var(--i) * .5s);}
  .vl-log-row .vl-tick path{stroke-dasharray:20;stroke-dashoffset:20;
    animation:vl-draw .4s ease forwards;animation-delay:calc(.8s + var(--i) * .5s);}
}
@keyframes vl-in{to{opacity:1;transform:none;}}
@keyframes vl-draw{to{stroke-dashoffset:0;}}

.vl-split{display:grid;grid-template-columns:1fr 1fr;gap:72px;align-items:start;}
.vl-split .vl-h2{margin-bottom:24px;}
.vl-split .vl-lead + .vl-lead{margin-top:18px;}
.vl-rules-title{font:600 14px/1.4 var(--sans);color:var(--gold-deep);margin-bottom:6px;}
.vl-rules li{padding:18px 0;border-bottom:1px solid var(--line);font-family:var(--serif);font-size:1.375rem;line-height:1.25;}
.vl-rules li:first-child{border-top:1px solid var(--line);}
.vl-dark .vl-rules li{border-color:var(--line-ink);}
.vl-dark .vl-rules-title{color:var(--gold);}

.vl-rule-item{padding:18px 0;border-bottom:1px solid var(--line);}
.vl-rule-item:first-child{border-top:1px solid var(--line);}
.vl-rule-header{display:flex;align-items:center;justify-content:space-between;gap:16px;}
.vl-rule-name{font-family:var(--serif);font-size:1.375rem;line-height:1.25;}
.vl-rule-toggle{flex:none;width:32px;height:32px;border-radius:50%;border:1.5px solid var(--ink);
  background-color:transparent;
  background-image:linear-gradient(var(--ink),var(--ink));background-repeat:no-repeat;
  background-origin:border-box;background-position:left center;background-size:0% 100%;
  color:var(--ink);font:400 20px/1 var(--sans);cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s,transform .3s;}
.vl-rule-toggle:hover,.vl-rule-toggle:focus-visible{background-size:100% 100%;color:var(--paper);}
.vl-rule-toggle[aria-expanded="true"]{transform:rotate(180deg);background-size:100% 100%;color:var(--gold);}
.vl-rule-desc{margin-top:12px;font-size:15px;line-height:1.6;color:var(--slate);
  max-width:52ch;animation:vl-fade .25s ease;font-family:var(--sans);}
@keyframes vl-fade{from{opacity:0;transform:translateY(-4px);}to{opacity:1;transform:none;}}

.vl-statement{font-family:var(--serif);font-size:clamp(1.5rem,2.6vw,2.125rem);line-height:1.3;
  letter-spacing:-.01em;max-width:30em;margin:24px 0 64px;}
.vl-caps-intro{font-size:14px;font-weight:600;color:var(--gold-deep);margin-bottom:18px;}
.vl-cap-row{display:grid;grid-template-columns:repeat(5,1fr);gap:0;}
.vl-cap-row li{padding:22px 24px 8px 0;margin-right:24px;border-top:2px solid var(--ink);
  font-family:var(--serif);font-size:1.25rem;line-height:1.3;}

.vl-flag-head{max-width:46rem;margin-bottom:56px;}
.vl-flag-head .vl-h2{margin-bottom:16px;}
.vl-flag-format{font:400 14px/1.5 var(--mono);color:var(--gold-hi);}
.vl-timeline{display:grid;grid-template-columns:4fr 2fr 1.4fr;gap:6px;}
.vl-seg-bar{height:12px;background:var(--gold);}
.vl-seg[data-tone="2"] .vl-seg-bar{background:rgba(200,162,74,.62);}
.vl-seg[data-tone="3"] .vl-seg-bar{background:transparent;border:1.5px solid var(--gold);}
.vl-seg-size{font-family:var(--serif);font-size:clamp(2rem,3.4vw,2.75rem);line-height:1;margin:22px 0 10px;letter-spacing:-.02em;}
.vl-seg-label{font-size:15px;color:var(--on-ink-mute);max-width:22ch;}
.vl-outcome{margin-top:64px;padding-top:28px;border-top:1px solid var(--line-ink);
  display:grid;grid-template-columns:1fr auto;gap:32px;align-items:end;}
.vl-outcome p{max-width:52ch;}
.vl-outcome strong{font-weight:600;color:var(--gold-hi);}
.vl-fine{font-size:13px;color:var(--on-ink-mute);margin-top:14px;max-width:62ch;}

.vl-learn-head{max-width:38rem;margin-bottom:56px;}
.vl-learn-grid{display:grid;grid-template-columns:1fr 1fr;border-top:1px solid var(--line);}
.vl-learn-item{padding:36px 40px 40px 0;border-bottom:1px solid var(--line);}
.vl-learn-item:nth-child(even){padding-left:40px;padding-right:0;border-left:1px solid var(--line);}
.vl-learn-item .vl-h3{font-size:1.875rem;margin-bottom:10px;}

.vl-model-head{max-width:40rem;margin-bottom:56px;}
.vl-path{display:grid;grid-template-columns:repeat(8,1fr);counter-reset:step;}
.vl-path li{position:relative;padding:26px 14px 0 0;border-top:1.5px solid var(--ink);}
.vl-path li::before{content:"";position:absolute;top:-6px;left:0;width:10px;height:10px;border-radius:50%;background:var(--gold);}
.vl-path li:last-child{border-top-color:transparent;}
.vl-path-n{display:block;font:400 12.5px/1 var(--mono);color:var(--slate);margin-bottom:10px;}
.vl-path-t{font-family:var(--serif);font-size:1.25rem;line-height:1.2;}

.vl-lab-grid{display:grid;grid-template-columns:1fr 1fr;gap:72px;align-items:center;}
.vl-lab-grid .vl-h2{margin-bottom:16px;}
.vl-lab-grid .vl-lead{color:var(--on-ink-mute);margin-bottom:36px;}
.vl-lab-focus{margin-bottom:40px;}
.vl-lab-focus li{padding:12px 0;border-bottom:1px solid var(--line-ink);font-size:16px;}
.vl-lab-focus li:first-child{border-top:1px solid var(--line-ink);}
.vl-principle{font-family:var(--serif);font-weight:500;font-size:clamp(3rem,7.2vw,5.75rem);line-height:.98;letter-spacing:-.03em;}
.vl-principle span{display:block;}

.vl-college .vl-statement{margin-bottom:28px;}

.vl-final{background:var(--gold);color:var(--ink);padding:96px 0;}
.vl-final .vl-h2{font-size:clamp(2.1rem,4.6vw,3.6rem);max-width:19em;margin-bottom:40px;}

.vl-footer{background:var(--ink-3);color:var(--on-ink);padding:56px 0 40px;}
.vl-footer-row{display:flex;flex-wrap:wrap;justify-content:space-between;gap:32px;padding-bottom:36px;border-bottom:1px solid var(--line-ink);}
.vl-footer p{font-size:14px;color:var(--on-ink-mute);max-width:38ch;margin-top:14px;}
.vl-footer nav{display:flex;flex-wrap:wrap;gap:12px 28px;align-items:flex-start;}
.vl-footer nav a{font-size:13px;letter-spacing:.08em;text-decoration:none;color:var(--on-ink-mute);}
.vl-footer nav a:hover{color:var(--on-ink);}
.vl-footer .vl-copy{padding-top:24px;margin-top:0;max-width:none;font-size:13px;color:var(--on-ink-mute);}

@media (max-width:1000px){
  .vl-cap-row{grid-template-columns:repeat(2,1fr);row-gap:8px;}
  .vl-path{grid-template-columns:repeat(4,1fr);row-gap:36px;}
  .vl-path li:last-child{border-top-color:var(--ink);}
}
@media (max-width:860px){
  .vl-section{padding:72px 0;}
  .vl-hero{padding:56px 0 72px;}
  .vl-hero-grid,.vl-split,.vl-lab-grid{grid-template-columns:1fr;gap:48px;}
  .vl-visual.has-photo .vl-log{margin:-40px 0 0 0;width:calc(100% - 24px);}
  .vl-timeline{grid-template-columns:1fr;gap:36px;}
  .vl-seg-bar{width:var(--w);}
  .vl-seg-size{margin-top:16px;}
  .vl-outcome{grid-template-columns:1fr;align-items:start;}
  .vl-learn-grid{grid-template-columns:1fr;}
  .vl-learn-item,.vl-learn-item:nth-child(even){padding:28px 0;border-left:0;}
  .vl-statement{margin-bottom:44px;}
}
@media (max-width:560px){
  .vl-wrap{padding:0 20px;}
  .vl-actions .vl-btn{width:100%;}
  .vl-cap-row{grid-template-columns:1fr;}
  .vl-cap-row li{margin-right:0;}
  .vl-path{grid-template-columns:repeat(2,1fr);}
  .vl-log-row{flex-wrap:wrap;row-gap:4px;}
  .vl-log-state{flex-basis:100%;text-align:left;padding-left:36px;}
}
`;

/* ───────────────────────── SMALL PIECES ───────────────────────── */

const Tick = () => (
  <span className="vl-tick" aria-hidden="true">
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2.5 6.4L5 8.9L9.6 3.6" stroke="#E0BC62" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

// Fixed positions (not random) so server and browser render the same thing.
const heroDots = [
  { x: "6%", y: "16%", s: 3, d: "0s", dur: "4.2s" },
  { x: "14%", y: "62%", s: 2, d: "1.1s", dur: "5s" },
  { x: "22%", y: "34%", s: 3, d: "2.3s", dur: "4.6s" },
  { x: "31%", y: "84%", s: 2, d: ".6s", dur: "5.4s" },
  { x: "39%", y: "12%", s: 2, d: "1.8s", dur: "4s" },
  { x: "47%", y: "58%", s: 3, d: "3s", dur: "5.2s" },
  { x: "55%", y: "26%", s: 2, d: ".3s", dur: "4.8s" },
  { x: "62%", y: "88%", s: 3, d: "2s", dur: "4.4s" },
  { x: "68%", y: "8%", s: 2, d: "1.4s", dur: "5.6s" },
  { x: "74%", y: "46%", s: 3, d: "2.7s", dur: "4.2s" },
  { x: "81%", y: "72%", s: 2, d: ".9s", dur: "5s" },
  { x: "88%", y: "20%", s: 3, d: "3.4s", dur: "4.6s" },
  { x: "93%", y: "58%", s: 2, d: "1.6s", dur: "5.4s" },
  { x: "97%", y: "90%", s: 2, d: "2.5s", dur: "4.8s" },
];

const HEX_POINTS = "50,2 93,26 93,74 50,98 7,74 7,26";

// Decorative background layer for the hero: soft gold glow, drifting hexagon outlines, twinkling dots.
function HeroFx() {
  return (
    <div className="vl-hero-fx" aria-hidden="true">
      <span className="vl-hero-glow" />
      {["vl-hex-1", "vl-hex-2", "vl-hex-3"].map((c) => (
        <svg key={c} className={`vl-hex ${c}`} viewBox="0 0 100 100">
          <polygon points={HEX_POINTS} />
        </svg>
      ))}
      {heroDots.map((p, i) => (
        <span
          key={i}
          className="vl-dot"
          style={{ "--x": p.x, "--y": p.y, "--s": `${p.s}px`, "--d": p.d, "--dur": p.dur }}
        />
      ))}
    </div>
  );
}

const VALMIKI_ROUTES = {
  home: "/valmiki",
  about: "/valmiki/about",
  program: "/valmiki/program",
  lab: "/valmiki/lab",
  partner: "/valmiki/partner",
  contact: "/valmiki/contact",
};

const VALMIKI_NAV_LINKS = [
  { label: "HOME", href: VALMIKI_ROUTES.home, current: true },
  { label: "ABOUT VALMIKI", href: VALMIKI_ROUTES.about },
  { label: "PROGRAM", href: VALMIKI_ROUTES.program },
  { label: "VALMIKI AI LAB", href: VALMIKI_ROUTES.lab },
  { label: "CONTACT", href: VALMIKI_ROUTES.contact },
];

const ValmikiBrandMark = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
    <circle cx="15" cy="15" r="14" stroke="#C8A24A" strokeWidth="1.2" />
    <path d="M9 19L15 8L21 19" stroke="#F3F5F8" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="15" cy="15.5" r="1.6" fill="#C8A24A" />
  </svg>
);

/* ───────────────────────── PAGE ───────────────────────── */

export default function ValmikiHome() {
  const [openApproach, setOpenApproach] = useState(null);
  const toggleApproach = (i) =>
    setOpenApproach(openApproach === i ? null : i);

  return (
    <div className="vl-root" id="top">
      <style>{css}</style>

      <main>
        {/* SECTION 1 — HERO */}
        <section className="vl-hero" aria-labelledby="vl-hero-title">
          <HeroFx />
          <div className="vl-wrap vl-hero-grid">
            <div>
              <h1 className="vl-h1" id="vl-hero-title">
                <span style={{ "--i": 0 }}>Build AI Skills.</span>
                <span style={{ "--i": 1 }}>Build Real Projects.</span>
                <span style={{ "--i": 2 }}>Build Your Career.</span>
              </h1>
              <p className="vl-lead">
                VALMIKI AI LAB is an applied AI learning and career development ecosystem designed to help
                students build practical capabilities in Generative AI, AI Automation and Agentic AI.
              </p>
              <div className="vl-actions">
                <a className="vl-btn vl-btn-gold" href={VALMIKI_ROUTES.program}>Explore the Program</a>
                <a className="vl-btn vl-btn-line-light" href={VALMIKI_ROUTES.partner}>Partner With Us</a>
              </div>
            </div>

            <div className={`vl-visual${HERO_IMAGE ? " has-photo" : ""}`}>
              {HERO_IMAGE && <img className="vl-photo" src={HERO_IMAGE} alt={HERO_IMAGE_ALT} />}
              <aside className="vl-log" aria-label="Example of the portfolio evidence a student builds">
                <div className="vl-log-head">
                  <span className="vl-log-title">Portfolio evidence</span>
                  <span className="vl-log-tag">illustrative</span>
                </div>
                <ul>
                  {evidenceRows.map((r, i) => (
                    <li key={r.title} className="vl-log-row" style={{ "--i": i }}>
                      <Tick />
                      <span className="vl-log-name">{r.title}</span>
                      <span className="vl-log-state">{r.state}</span>
                    </li>
                  ))}
                </ul>
                <p className="vl-log-foot">Every project is documented as evidence of real work.</p>
              </aside>
            </div>
          </div>
        </section>

        {/* SECTION 2 — WHAT IS VALMIKI? */}
        <section className="vl-section" aria-labelledby="vl-what">
          <div className="vl-wrap vl-split">
            <div>
              <h2 className="vl-h2" id="vl-what">What is VALMIKI?</h2>
              <p className="vl-lead">
                VALMIKI AI LAB is built to help students move beyond learning about AI and start applying
                AI to real problems, workflows and projects.
              </p>
            </div>
            <div>
              <p className="vl-rules-title">Our approach combines</p>
              <ul className="vl-rules">
                {approach.map((a, i) => (
                  <li key={a.title} className="vl-rule-item">
                    <div className="vl-rule-header">
                      <span className="vl-rule-name">{a.title}</span>
                      <button
                        type="button"
                        className="vl-rule-toggle"
                        onClick={() => toggleApproach(i)}
                        aria-expanded={openApproach === i}
                        aria-controls={`vl-rule-desc-${i}`}
                        aria-label={`Toggle details for ${a.title}`}
                      >
                        {openApproach === i ? "−" : "+"}
                      </button>
                    </div>
                    {openApproach === i && (
                      <p className="vl-rule-desc" id={`vl-rule-desc-${i}`}>
                        {a.desc}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 3 — WHY AI, WHY NOW? */}
        <section className="vl-section vl-white" aria-labelledby="vl-why">
          <div className="vl-wrap">
            <h2 className="vl-h2" id="vl-why">Why AI, why now?</h2>
            <p className="vl-statement">
              The future of work is changing. Students need more than AI awareness — they need the ability
              to apply technology responsibly and practically.
            </p>
            <p className="vl-caps-intro">So they can</p>
            <ul className="vl-cap-row">
              {capabilities.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </div>
        </section>

        {/* SECTION 4 — FLAGSHIP PROGRAM */}
        <section className="vl-section vl-dark" aria-labelledby="vl-flagship">
          <div className="vl-wrap">
            <div className="vl-flag-head">
              <h2 className="vl-h2" id="vl-flagship">AI Career Development Program</h2>
              <p className="vl-flag-format">2 days per week, 3 hours per session</p>
            </div>

            <ol className="vl-timeline" aria-label="Program stages, in order">
              {programSegments.map((s) => (
                <li key={s.size} className="vl-seg" data-tone={s.tone} style={{ "--w": s.w }}>
                  <div className="vl-seg-bar" />
                  <p className="vl-seg-size">{s.size}</p>
                  <p className="vl-seg-label">{s.label}</p>
                </li>
              ))}
            </ol>

            <div className="vl-outcome">
              <div>
                <p className="vl-lead">
                  <strong>Outcome:</strong> assessment, certification for eligible students and
                  career/placement assistance.
                </p>
                <p className="vl-fine">
                  Career outcomes and opportunities vary by employer, market demand, student performance,
                  skills, portfolio and location.
                </p>
              </div>
              <a className="vl-btn vl-btn-gold" href={VALMIKI_ROUTES.program}>Explore the Program</a>
            </div>
          </div>
        </section>

        {/* SECTION 5 — WHAT STUDENTS LEARN */}
        <section className="vl-section" aria-labelledby="vl-learn">
          <div className="vl-wrap">
            <div className="vl-learn-head">
              <h2 className="vl-h2" id="vl-learn">What students learn</h2>
            </div>
            <ul className="vl-learn-grid">
              {learnAreas.map((a) => (
                <li key={a.title} className="vl-learn-item">
                  <h3 className="vl-h3">{a.title}</h3>
                  <p className="vl-muted">{a.desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* SECTION 6 — OUR LEARNING MODEL */}
        <section className="vl-section vl-white" aria-labelledby="vl-model">
          <div className="vl-wrap">
            <div className="vl-model-head">
              <h2 className="vl-h2" id="vl-model">Our learning model</h2>
            </div>
            <ol className="vl-path">
              {learningModel.map((step, i) => (
                <li key={step}>
                  <span className="vl-path-n">{String(i + 1).padStart(2, "0")}</span>
                  <span className="vl-path-t">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* SECTION 7 — VALMIKI AI LAB */}
        <section className="vl-section vl-dark" aria-labelledby="vl-lab">
          <div className="vl-wrap vl-lab-grid">
            <div>
              <h2 className="vl-h2" id="vl-lab">VALMIKI AI LAB</h2>
              <p className="vl-lead">
                A space for experimentation, applied learning and AI innovation.
              </p>
              <ul className="vl-lab-focus" aria-label="Lab focus areas">
                {labFocus.map((f) => <li key={f}>{f}</li>)}
              </ul>
              <a className="vl-btn vl-btn-gold" href={VALMIKI_ROUTES.lab}>Explore VALMIKI AI LAB</a>
            </div>
            <p className="vl-principle" aria-label="Experiment. Build. Test. Improve.">
              <span>Experiment.</span>
              <span>Build.</span>
              <span>Test.</span>
              <span>Improve.</span>
            </p>
          </div>
        </section>

        {/* SECTION 8 — FOR COLLEGES */}
        <section className="vl-section" aria-labelledby="vl-colleges">
          <div className="vl-wrap vl-college">
            <h2 className="vl-h2" id="vl-colleges">For colleges</h2>
            <p className="vl-statement">
              Build an AI-ready learning ecosystem without creating a separate AI academic infrastructure.
            </p>
            <p className="vl-lead vl-muted">
              VALMIKI brings curriculum, specialist faculty, practical learning, projects, internship
              frameworks and career readiness into the college ecosystem.
            </p>
            <div className="vl-actions" style={{ marginTop: 34 }}>
              <a className="vl-btn vl-btn-ink" href={VALMIKI_ROUTES.partner}>Partner With VALMIKI</a>
            </div>
          </div>
        </section>

        {/* SECTION 9 — FINAL CTA */}
        <section className="vl-final" aria-labelledby="vl-final">
          <div className="vl-wrap">
            <h2 className="vl-h2" id="vl-final">
              The future will belong to people who know how to work with AI.
            </h2>
            <div className="vl-actions">
              <a className="vl-btn vl-btn-ink" href={VALMIKI_ROUTES.program}>Explore the Program</a>
              <a className="vl-btn vl-btn-line-ink" href={VALMIKI_ROUTES.partner}>Partner With Us</a>
            </div>
          </div>
        </section>
      </main>

      <ValmikiFooter />

      {/* Lead popup — ValmikiLeadForm opens itself after OPEN_AFTER_MS (see that file) */}
      <ValmikiLeadForm />
    </div>
  );
}